import { AlignmentType, IRunOptions, Paragraph, TextRun } from 'docx';

const BLOCKQUOTE_INDENT = 720;

const getTextRunsFromNode = (
  node: ChildNode,
  inheritedStyles: Partial<IRunOptions> = {},
): TextRun[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.replace(/\u00a0/g, ' ') ?? '';

    if (!text.trim()) return [];

    return [
      new TextRun({
        text,
        ...inheritedStyles,
      }),
    ];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const nextStyles = { ...inheritedStyles };

  if (tagName === 'strong' || tagName === 'b') nextStyles.bold = true;
  if (tagName === 'em' || tagName === 'i') nextStyles.italics = true;
  if (tagName === 'u') nextStyles.underline = {};

  if (tagName === 'br') {
    return [new TextRun({ text: '', break: 1, ...inheritedStyles })];
  }

  return Array.from(element.childNodes).flatMap((child) => getTextRunsFromNode(child, nextStyles));
};

const collectBlockElements = (element: HTMLElement): HTMLElement[] => {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'div') {
    const children = Array.from(element.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );

    return children.length ? children.flatMap(collectBlockElements) : [element];
  }

  return [element];
};

const splitChildNodesByBr = (nodes: ChildNode[]): ChildNode[][] =>
  nodes
    .reduce<ChildNode[][]>(
      (groups, node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as HTMLElement).tagName.toLowerCase() === 'br'
        ) {
          return [...groups, []];
        }
        const last = groups[groups.length - 1];
        return [...groups.slice(0, -1), [...last, node]];
      },
      [[]],
    )
    .filter((group) => group.length > 0);

const getParagraphsFromBlockquote = (element: HTMLElement): Paragraph[] => {
  const groups = splitChildNodesByBr(Array.from(element.childNodes));

  return groups.map(
    (group, i) =>
      new Paragraph({
        children: group.flatMap((node) => getTextRunsFromNode(node)),
        spacing: { after: i === groups.length - 1 ? 240 : 120 },
        indent: { left: BLOCKQUOTE_INDENT },
      }),
  );
};

const getParagraphsFromElement = (element: HTMLElement, index: number): Paragraph[] => {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'blockquote') {
    return getParagraphsFromBlockquote(element);
  }

  const isListItem = tagName === 'li';
  const isOrderedListItem = isListItem && element.parentElement?.tagName.toLowerCase() === 'ol';
  const prefix = isListItem ? `${isOrderedListItem ? `${index + 1}.` : '•'} ` : '';
  const isTitle = tagName === 'h1' || element.classList.contains('manifestation-title');
  const baseStyles: Partial<IRunOptions> = isTitle ? { bold: true, size: 30 } : {};

  const textRuns = element.childNodes.length
    ? Array.from(element.childNodes).flatMap((child) => getTextRunsFromNode(child, baseStyles))
    : [];

  const children =
    prefix || textRuns.length
      ? [...(prefix ? [new TextRun({ text: prefix })] : []), ...textRuns]
      : [new TextRun(' ')];

  return [
    new Paragraph({
      children,
      spacing: { after: isTitle ? 320 : 240 },
      ...(isTitle && { alignment: AlignmentType.CENTER }),
    }),
  ];
};

export const getDocxParagraphsFromEditor = (editorElement: HTMLDivElement) => {
  const topLevelChildren = Array.from(editorElement.children).filter(
    (node): node is HTMLElement => node instanceof HTMLElement,
  );

  const blockElements = topLevelChildren.flatMap(collectBlockElements);

  if (!blockElements.length) {
    const text = editorElement.innerText.trim();

    if (!text) return [];

    return text
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map(
        (line) =>
          new Paragraph({
            children: [new TextRun(line)],
            spacing: { after: 240 },
          }),
      );
  }

  return blockElements.flatMap((element, index) => getParagraphsFromElement(element, index));
};
